import { FormEventHandler, startTransition } from 'react'
import { ActionObject, useAction } from './use-action/UseAction'
import { setupSubmitFailedCustomEvent, setupSubmitSuccessCustomEvent } from '../helpers/setupCustomEvents'
import { SUBMIT_FAILED_EVENT, SUBMIT_SUCCESS_EVENT } from '../constants/custom-event-names'
import { useSchema, UseSchemaOptions } from './use-schema/UseSchema'
import { useValidateSchema, UseValidateSchemaOptions } from './use-validate-schema/UseValidateSchema'
import { useEventDispatchQueue } from './UseEventDispatchQueue'

interface UseFormOptions extends UseSchemaOptions, UseValidateSchemaOptions {
    dispatchDelayForActionEvents?: number
}

interface Props<TState> {
    formId: string
    action: ActionObject<TState>
    initialState: TState
    options?: UseFormOptions
}

/**
 * @description useForm can be used to automate validating and submitting of forms. It
 * uses the following hooks to automate things:
 * - useAction handles calling actions, and updating state
 * - useSchema handles generating of forms
 * - useValidateSchema handles validating of forms and returning validation messages
 *
 * each of these hooks can be used on it's own if necessary
 *
 * @fires SubmitFailed when validation errors where generated
 * @fires SubmitSuccess otherwise
 * @fired ActionFailed (from useAction)
 * @fires ActionSuccess (from useAction)
 * @fires ActionEnded (from useAction)
 *
 * @param options.excludeFieldNames -- read comments on useSchema.
 * @param options.appendFieldRules --  read comments on useSchema. IMPORTANT: async field rules
 * are currently not supported.
 * @param options.customSchema -- read comments on useSchema.
 * @param options.dispatchDelayForPartialValidationEvents -- read comments on useValidateSchema
 * @param options.fieldsWithCustomValidation -- read comments on useValidateSchema
 *
 * @returns object.state -- the state that is returned from the action. this object is stateful
 * @returns object.formAction -- place this in the form element's action attribute
 * @returns object.onSubmit -- place this in the form element's onSubmit attribute if you want to
 * validate the form before submitting it. On successful validation it prevents the default form
 * submission and invokes formAction inside startTransition. That opts out of React 19's automatic
 * reset of uncontrolled inputs after a form action, so field values stay in the DOM after save.
 * @returns object.regenerateSchema -- if there was a change in the composition of your form because of
 * a user action, you can use this function to regenerate the schema. The schema will be regenerated
 * two event loop frames after this function is called, awaiting the correct state of the form.
 *
 * @example minimal
 * ```
 * const { state, formAction, onSubmit } = useForm({formId: 'form-id', action: ()=>state, initialState})
 * <Form id="form-id" onSubmit={onSubmit} action={formAction}>...</Form>
 * ```
 *
 * @example append field rules
 * ```
 * const appendFieldRules = {['my-number-field']: (numberSchema: NumberSchema) => numberSchema.integer()}
 *
 * const { state, formAction, onSubmit } = useForm({formId: 'form-id', action: ()=>state, initialState, options: {appendFieldRules}})
 * <Form id="form-id" onSubmit={onSubmit} action={formAction}>...</Form>
 * ```
 *
 * @example replace field rules
 * ```
 * const appendFieldRules = {['my-number-field']: (_: NumberSchema) => number().min().integer()}
 * const { state, formAction, onSubmit } = useForm({formId: 'form-id', action: ()=>state, initialState, options: {appendFieldRules}})
 * <Form id="form-id" onSubmit={onSubmit} action={formAction}>...</Form>
 * ```
 *
 * @example exclude fields
 * ```
 * const { state, formAction, onSubmit } = useForm({formId: 'form-id', action: ()=>state, initialState, options: {excludeFieldNames: ['zip-code-field']}})
 * <Form id="form-id" onSubmit={onSubmit} action={formAction}>...</Form>
 * ```
 * */
export const useForm = <TState>({ formId, action, initialState, options }: Props<TState>) => {
    const { schema, regenerateSchema } = useSchema(formId, options)
    const fieldsWithCustomValidation = options?.appendFieldRules ? Object.keys(options?.appendFieldRules) : undefined
    const { validate } = useValidateSchema(formId, schema, { fieldsWithCustomValidation })
    const { state, formAction } = useAction(action, initialState, formId)
    const eventDispatchQueue = useEventDispatchQueue()

    const onSubmit: FormEventHandler<HTMLFormElement> = (event) => {
        // Always take over submission: React 19 resets uncontrolled inputs after a form `action`
        // completes. Calling formAction from onSubmit (with preventDefault) keeps DOM values.
        event.preventDefault()

        const formElement = event.currentTarget
        const errors = validate()
        if (!!errors?.length) {
            const dispatchId = `${formId}-${SUBMIT_FAILED_EVENT}`
            eventDispatchQueue.queueEvent(
                dispatchId,
                setupSubmitFailedCustomEvent(formId, errors),
                formElement,
                0,
            )
            return
        }

        const dispatchId = `${formId}-${SUBMIT_SUCCESS_EVENT}`
        eventDispatchQueue.queueEvent(dispatchId, setupSubmitSuccessCustomEvent(formId), formElement, 0)

        startTransition(() => {
            formAction(new FormData(formElement))
        })
    }

    return { state, formAction, onSubmit, regenerateSchema }
}
